// Runtime UI translator: translates visible hard-coded labels using tenant "ui.<text>" translations and reports unknown labels for AI translation.
import { api } from "@/lib/api";

const SKIP = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "SELECT", "OPTION", "CODE", "PRE", "SVG"]);
const UI_RE = /^[A-Za-z][A-Za-z0-9 ,.'’&/()%:+\-—–?!]{1,199}$/;
const state = { nodes: new Map(), attrs: new Map(), pending: new Set(), known: new Set(), observer: null, timer: null, sendTimer: null, dict: {}, active: false };

const skipEl = (el) => { for (let e = el; e && e !== document.body; e = e.parentElement) { if (SKIP.has(e.tagName) || e.dataset?.notranslate !== undefined || e.classList?.contains("font-mono")) return true; } return false; };
const collectable = (t) => UI_RE.test(t) && !/\d{5,}/.test(t) && !t.includes("@") && (t.match(/\d/g) || []).length <= 4;

function translateNode(node) {
  const raw = node.nodeValue;
  const t = raw.trim();
  if (!t) return;
  const rec = state.nodes.get(node);
  if (rec && rec.translated === raw) return;
  const tr = state.dict[`ui.${t}`];
  if (tr && tr !== t) {
    const out = raw.replace(t, tr);
    state.nodes.set(node, { orig: raw, translated: out });
    node.nodeValue = out;
  } else if (!state.known.has(t) && collectable(t)) {
    state.pending.add(t);
    state.known.add(t);
  }
}

function translateAttrs(el) {
  for (const a of ["placeholder", "title", "aria-label"]) {
    const v = el.getAttribute?.(a);
    if (!v) continue;
    const rec = state.attrs.get(el) || {};
    if (rec[a] === v) continue;
    const tr = state.dict[`ui.${v.trim()}`];
    if (tr) { rec[a] = tr; rec[`${a}_orig`] = v; state.attrs.set(el, rec); el.setAttribute(a, tr); }
    else if (!state.known.has(v.trim()) && collectable(v.trim())) { state.pending.add(v.trim()); state.known.add(v.trim()); }
  }
}

function walk(root) {
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, { acceptNode: (n) => (n.nodeType === 1 ? (SKIP.has(n.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT) : NodeFilter.FILTER_ACCEPT) });
  let n = w.currentNode;
  while (n) {
    if (n.nodeType === 3) { if (!skipEl(n.parentElement)) translateNode(n); } else if (n.nodeType === 1 && n !== root) translateAttrs(n);
    n = w.nextNode();
  }
  if (state.pending.size && !state.sendTimer) state.sendTimer = setTimeout(flush, 4000);
}

function flush() {
  state.sendTimer = null;
  const texts = [...state.pending].slice(0, 300);
  state.pending.clear();
  if (texts.length) api.post("/i18n/collect", { texts }).catch(() => {});
}

function schedule() { if (state.timer) return; state.timer = setTimeout(() => { state.timer = null; if (state.active) walk(document.body); }, 60); }

export function startDomTranslator(dict) {
  state.dict = dict || {};
  state.active = true;
  walk(document.body);
  if (!state.observer) {
    state.observer = new MutationObserver(schedule);
    state.observer.observe(document.body, { childList: true, characterData: true, subtree: true });
  }
}

export function stopDomTranslator() {
  state.active = false;
  state.observer?.disconnect();
  state.observer = null;
  for (const [node, rec] of state.nodes) if (node.nodeValue === rec.translated) node.nodeValue = rec.orig;
  for (const [el, rec] of state.attrs) for (const a of ["placeholder", "title", "aria-label"]) if (rec[`${a}_orig`] && el.getAttribute(a) === rec[a]) el.setAttribute(a, rec[`${a}_orig`]);
  state.nodes.clear();
  state.attrs.clear();
}
